/**
 * The one cost-of-living document: raw stored rows in, one HTML document out.
 *
 * This is deliberately a single implementation. The edge renderer bundles it
 * today, and the city-page integration the owner wires later imports the same
 * function rather than writing a second one, so the document a viewer sees at
 * the edge and the one a published page carries can never drift.
 *
 * Three rules the shape enforces:
 *
 * 1. A converted figure is only ever the output of `convertPrice`. When a leg is
 *    missing the cell reads `unavailable` - never a zero, never a guessed rate,
 *    never a number computed some other way.
 * 2. A row marked `convertible: false` (the mortgage interest rate, a percentage
 *    rather than money) is printed as the source's own figure with its unit and
 *    gets no converted value at all.
 * 3. Every value interpolated from the payload is HTML-escaped: the labels and
 *    the sections come from a third party and are not markup.
 *
 * It is also deterministic: no clock, no randomness, and every figure is
 * formatted through an explicit `en-GB` formatter, so the same payload and
 * target render byte-identically on any host in any timezone. CloudFront caches
 * one document per currency on the strength of that.
 */
import { isIso4217 } from "./currency.js";
import { convertPrice } from "./living-cost.js";
/** `45.58` -> `"45.58"`, and `1234.5` -> `"1,234.50"`. Locale and timezone fixed. */
const AMOUNT_FORMAT = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
/** Contributor and entry counts, grouped the same way on every host. */
const COUNT_FORMAT = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });
/**
 * Money formatters, one per currency. `currencyDisplay: "code"` (so `TRY 45.58`,
 * not a symbol that a font may not carry) and en-GB separators, both explicit
 * because the default locale follows the host and would break the cache.
 */
const MONEY_FORMAT = new Map();
function moneyFormatter(currency) {
    let formatter = MONEY_FORMAT.get(currency);
    if (!formatter) {
        formatter = new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency,
            currencyDisplay: "code",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        MONEY_FORMAT.set(currency, formatter);
    }
    return formatter;
}
/** An amount in `currency`; a non-finite amount is reported, never printed. */
function formatMoney(amount, currency) {
    if (!Number.isFinite(amount))
        return "unavailable";
    if (!isIso4217(currency))
        return AMOUNT_FORMAT.format(amount);
    // Intl separates the currency code with a non-breaking space. The document is
    // ASCII by contract, so it is normalised here, at the one formatting boundary,
    // rather than by every caller that would have to remember.
    return moneyFormatter(currency)
        .format(amount)
        .replace(/[\u00a0\u202f]/g, " ");
}
/** A figure whose unit is not a currency (the percentage row). */
function formatUnit(amount, unit) {
    if (!Number.isFinite(amount))
        return "unavailable";
    return `${AMOUNT_FORMAT.format(amount)}${unit}`;
}
function formatCount(value) {
    if (typeof value !== "number" || !Number.isFinite(value))
        return "unknown";
    return COUNT_FORMAT.format(value);
}
/**
 * A date as its UTC calendar day. `scrapedAt` is an ISO timestamp and slicing it
 * is the only rendering that cannot depend on the host timezone; an unparseable
 * value is shown as it arrived rather than invented.
 */
function formatDate(value) {
    if (!value)
        return "unknown";
    return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : value;
}
/** Escapes text interpolated into markup or an attribute. */
function escapeHtml(value) {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}
/**
 * `rent-per-month` -> `Rent per month`. A section with no name reads as "other".
 *
 * Exported because the surfaces that print these figures must agree on the
 * heading: the edge document and the public guide both group rows by the
 * publisher's own section.
 */
export function humaniseCostGroup(itemGroup) {
    const words = itemGroup.replace(/[-_]+/g, " ").trim();
    if (words === "")
        return "Other";
    return words.charAt(0).toUpperCase() + words.slice(1);
}
/** A row that is not money keeps its own unit and is never converted. */
function renderItem(item, payload, targetCurrency) {
    return {
        item,
        local: item.unit === null
            ? formatMoney(item.priceLocal, item.currency)
            : formatUnit(item.priceLocal, item.unit),
        converted: item.convertible
            ? convertPrice(item.priceLocal, item.currency, targetCurrency, payload.fxSnapshot)
            : null,
    };
}
/** One row of the item table. Both cells are text, never a number. */
function renderItemRow(row, targetCurrency) {
    const { item } = row;
    const converted = !item.convertible
        ? "not convertible"
        : row.converted === null
            ? "unavailable"
            : formatMoney(row.converted, targetCurrency);
    return (`<tr class="item" data-item="${escapeHtml(item.key)}">` +
        `<td class="item-label">${escapeHtml(item.label)}</td>` +
        `<td class="local">${escapeHtml(row.local)}</td>` +
        `<td class="converted">${escapeHtml(converted)}</td>` +
        `</tr>`);
}
/** The item table, grouped by the source's own section, in the order it arrived. */
function renderItemTable(payload, targetCurrency, rendered) {
    const groups = new Map();
    for (const item of payload.items) {
        const key = item.itemGroup || "other";
        const bucket = groups.get(key);
        if (bucket)
            bucket.push(item);
        else
            groups.set(key, [item]);
    }
    const rows = [];
    for (const [group, items] of groups) {
        rows.push(`<tr class="group"><th scope="colgroup" colspan="3">${escapeHtml(humaniseCostGroup(group))}</th></tr>`);
        for (const item of items) {
            const row = rendered.find((candidate) => candidate.item.key === item.key);
            if (row)
                rows.push(renderItemRow(row, targetCurrency));
        }
    }
    return [
        `<table>`,
        `<caption>Item prices for ${escapeHtml(payload.cityName)}</caption>`,
        `<thead><tr>`,
        `<th scope="col">Item</th>`,
        `<th scope="col">${escapeHtml(payload.currency)}</th>`,
        `<th scope="col">${escapeHtml(targetCurrency)}</th>`,
        `</tr></thead>`,
        `<tbody>`,
        ...rows,
        `</tbody>`,
        `</table>`,
    ].join("\n");
}
const STYLE = [
    ":root { color-scheme: light dark }",
    "body { font: 16px/1.5 -apple-system, Segoe UI, Roboto, sans-serif; margin: 0; padding: 2rem 1rem }",
    "main { margin: 0 auto; max-width: 60rem }",
    "h1 { font-size: 1.6rem; margin: 0 0 0.5rem }",
    ".meta, .facts, .conversion { font-size: 0.9rem }",
    ".stale { background: #b45309; border-radius: 999px; color: #fff; display: inline-block; font-size: 0.8rem; margin: 0 0 0.75rem; padding: 0.1rem 0.6rem }",
    "dl.facts { display: grid; grid-template-columns: max-content 1fr; gap: 0.1rem 0.75rem; margin: 1rem 0 }",
    "dl.facts dt { font-weight: 600 }",
    "dl.facts dd { margin: 0 }",
    "table { border-collapse: collapse; margin: 1rem 0; width: 100% }",
    "th, td { border-bottom: 1px solid #8884; padding: 0.35rem 0.5rem; text-align: left }",
    "td.local, td.converted, th:not(:first-child) { text-align: right }",
    "tr.group th { background: #8882; font-size: 0.85rem; text-transform: uppercase }",
    ".unavailable-marker { font-style: italic }",
].join("\n");
/**
 * Renders the complete document for one city, converted into `targetCurrency`.
 *
 * `targetCurrency` is the viewer's currency: the edge renderer resolves it from
 * the country header or the URL, and the caller may pass the city's own currency
 * to render the source's figures unconverted, in which case no conversion is
 * claimed anywhere in the document.
 */
export function renderLivingCostDocument(payload, targetCurrency) {
    // Whether any figure was actually converted: a page priced entirely in the
    // target currency carries no computed figures, so it must not claim the rate
    // source. The ECB's terms require the attribution wherever a conversion is
    // shown, and only there.
    const rendered = payload.items.map((item) => renderItem(item, payload, targetCurrency));
    // A conversion was actually applied when a row's own currency differs from the
    // target AND the rate map could express it. A city already priced in the target
    // needs no conversion, and a target with no stored leg produces none either — in
    // both cases the page carries no computed figure and must not claim the rate
    // source. The ECB requires that attribution wherever a conversion is shown, and
    // only there.
    const converted = rendered.some((row) => row.converted !== null && row.item.currency !== targetCurrency);
    const parts = [
        `<!doctype html>`,
        `<html lang="en">`,
        `<head>`,
        `<meta charset="utf-8">`,
        `<meta name="viewport" content="width=device-width, initial-scale=1">`,
        `<title>Cost of living in ${escapeHtml(payload.cityName)}</title>`,
        `<style>`,
        STYLE,
        `</style>`,
        `</head>`,
        `<body>`,
        `<main>`,
        `<h1>Cost of living in ${escapeHtml(payload.cityName)}</h1>`,
    ];
    if (payload.stale)
        parts.push(`<p class="stale" data-stale="true">Stale</p>`);
    parts.push(`<p class="meta">${escapeHtml(payload.cityName)}, ${escapeHtml(payload.country)}. ` +
        `Prices in ${escapeHtml(payload.currency)}` +
        (converted ? `, converted to ${escapeHtml(targetCurrency)}` : "") +
        `.</p>`, `<dl class="facts">`, `<dt>Scraped</dt><dd>${escapeHtml(formatDate(payload.scrapedAt))}</dd>`, `<dt>Source last updated</dt><dd>${escapeHtml(formatDate(payload.sourceUpdatedAt))}</dd>`, `<dt>Contributors</dt><dd>${escapeHtml(formatCount(payload.contributors))}</dd>`, `<dt>Entries (12 months)</dt><dd>${escapeHtml(formatCount(payload.entries12m))}</dd>`, `</dl>`);
    if (payload.items.length === 0) {
        parts.push(`<p class="empty">No readings yet for this city.</p>`);
    }
    else {
        parts.push(renderItemTable(payload, targetCurrency, rendered));
    }
    if (converted) {
        parts.push(`<p class="conversion">Converted prices are as of ${escapeHtml(formatDate(payload.fxAsOf))} ` +
            `and are <strong>computed</strong> with the euro reference rates published by the ` +
            `European Central Bank, applied to the rate map stored with this scrape. A price the map ` +
            `cannot express is shown as <span class="unavailable-marker">unavailable</span>.</p>`);
    }
    parts.push(`</main>`, `</body>`, `</html>`, ``);
    return parts.join("\n");
}
//# sourceMappingURL=living-cost-document.js.map